// client/src/components/shops/ShopsLanding.jsx
//
// The landing page at on-screen.app/shops, for independent skate, sneaker
// and streetwear shops in the Twin Cities. Plain, understated copy about
// running events: promote, run, post, archive. It deliberately never
// mentions the broad homepage's audiences and isn't linked from the main
// site nav; it's reached from shop bios, stickers and event pages.
//
// Copy is English-only on purpose: the page is for one local market, so it
// isn't routed through i18n.
//
// Shops, archive examples and the featured event link come from
// data/foundingShops.js — never invented. The shops wall renders only once
// at least MIN_SHOPS_FOR_WALL real shops are listed, and shows only those
// shops. The free-for-life deal card is hidden behind SHOW_DEAL.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { postJson } from '../../services/http';
import {
  FOUNDING_SHOPS,
  ARCHIVE_PAGES,
  FEATURED_EVENT_URL,
  FOUNDER_INSTAGRAM,
} from '../../data/foundingShops';
import './ShopsLanding.css';

const LOGO = `${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-h160.png`;

/** The shops wall stays hidden until this many real shops are listed. */
const MIN_SHOPS_FOR_WALL = 3;

/** The free-for-life deal card. Hidden for now; flip to bring it back. */
const SHOW_DEAL = false;

const FEATURES = [
  {
    tag: 'Promote',
    title: 'Promote the event.',
    body: 'Schedule announcements to every channel and answer comments and DMs from one inbox.',
  },
  {
    tag: 'Run',
    title: 'Run the event.',
    body: 'Raffles, contests, auctions, ticketed events, and check-in at the door.',
  },
  {
    tag: 'Post',
    title: 'Post the event.',
    body: 'Share results, winners, and photos to every channel when it’s done.',
  },
  {
    tag: 'Archive',
    title: 'Share the archive.',
    body: 'Every event gets a permanent public page you can link to.',
  },
];

const DEAL = [
  'Every feature, free for as long as On-Screen exists',
  'Your first raffle or event set up with you, in person',
  'Your name and logo on the Founding Shops wall',
  'A real say in what gets built next',
];

const ARCHIVE_ANATOMY = [
  ['The date', 'When the event happened.'],
  ['The details', 'Photos, video, and what took place.'],
  ['The results', 'Winners and standings, public for anyone to check.'],
  ['The shop', 'Linked back to the shop\'s own archive.'],
];

const EMPTY_FORM = { shopName: '', instagram: '', nextEventDate: '', contact: '', website: '' };

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function ShopsLanding() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.classList.add('shops-page-active');
    const prevTitle = document.title;
    document.title = 'On-Screen for shops';
    return () => {
      document.body.classList.remove('shops-page-active');
      document.title = prevTitle;
    };
  }, []);

  // Anonymous visit beacon for the super-admin Site Visits card.
  useEffect(() => {
    postJson('/api/tracking/site-visit', { path: '/shops' }).catch(() => {});
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await postJson('/api/founding-shops/claims', form);
      setStatus('sent');
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.message || 'That didn\'t go through. Try again, or DM us on Instagram.');
    }
  }

  const showWall = FOUNDING_SHOPS.length >= MIN_SHOPS_FOR_WALL;

  return (
    <div className="shops-page">
      <nav className="shops-nav" aria-label="Shops page">
        <div className="shops-wrap shops-nav-inner">
          <a href="#top" className="shops-nav-brand" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src={LOGO} alt="On-Screen" />
            <span>for shops</span>
          </a>
          <div className="shops-nav-actions">
            <Link to="/login" className="shops-nav-link">Sign in</Link>
            <button type="button" className="shops-btn shops-btn--primary shops-btn--sm" onClick={() => scrollToId('claim')}>
              Claim a spot
            </button>
          </div>
        </div>
      </nav>

      <main id="top">
        {/* Hero */}
        <header className="shops-hero">
          <div className="shops-wrap">
            <p className="shops-kicker">Twin Cities · Skate · Sneakers · Streetwear</p>
            <h1 className="shops-hero-title">
              Promote the event.<br />
              Run the event.<br />
              Post the event.<br />
              <span className="shops-accent">Share the archive.</span>
            </h1>
            <p className="shops-hero-sub">
              Event tools for independent shops in the Twin Cities.
            </p>
            <div className="shops-hero-actions">
              <button type="button" className="shops-btn shops-btn--primary" onClick={() => scrollToId('claim')}>
                Claim a spot
              </button>
              {FEATURED_EVENT_URL ? (
                <a className="shops-btn shops-btn--ghost" href={FEATURED_EVENT_URL}>See a live event page</a>
              ) : (
                <button type="button" className="shops-btn shops-btn--ghost" onClick={() => scrollToId('archive')}>
                  See how the archive works
                </button>
              )}
            </div>
          </div>
        </header>

        {/* What it does */}
        <section className="shops-section" aria-labelledby="shops-features-h">
          <div className="shops-wrap">
            <h2 id="shops-features-h" className="shops-visually-hidden">What it does</h2>
            <div className="shops-features">
              {FEATURES.map((f) => (
                <article key={f.title} className="shops-card shops-feature">
                  <span className="shops-tag">{f.tag}</span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* The archive */}
        <section id="archive" className="shops-section shops-section--band" aria-labelledby="shops-archive-h">
          <div className="shops-wrap shops-archive">
            <div className="shops-archive-copy">
              <h2 id="shops-archive-h">The archive</h2>
              <p>
                Every event gets a permanent public page with the date, the details, the results,
                and the shop that ran it. It stays up after the posts scroll away.
              </p>
            </div>

            {ARCHIVE_PAGES.length > 0 ? (
              <ul className="shops-archive-list">
                {ARCHIVE_PAGES.map((p) => (
                  <li key={p.url}>
                    <a className="shops-card shops-archive-page" href={p.url}>
                      {p.image && <img src={p.image} alt="" loading="lazy" />}
                      <span className="shops-archive-date">{p.date}</span>
                      <strong>{p.title}</strong>
                      <span className="shops-archive-shop">{p.shop}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="shops-card shops-anatomy" aria-label="What goes on an archive page">
                <p className="shops-anatomy-label">Every page carries</p>
                <ol>
                  {ARCHIVE_ANATOMY.map(([k, v]) => (
                    <li key={k}><strong>{k}</strong><span>{v}</span></li>
                  ))}
                </ol>
                <p className="shops-anatomy-foot">Example pages will appear here as shops run events.</p>
              </div>
            )}
          </div>
        </section>

        {/* Shops on On-Screen — only once enough real shops exist */}
        {showWall && (
          <section className="shops-section" aria-labelledby="shops-wall-h">
            <div className="shops-wrap">
              <h2 id="shops-wall-h">Shops on On-Screen</h2>
              <p className="shops-lede">Independent shops in the Twin Cities running their events here.</p>
              <ul className="shops-wall">
                {FOUNDING_SHOPS.map((shop) => (
                  <li key={shop.name}>
                    <a
                      className="shops-slot shops-slot--filled"
                      href={shop.archiveUrl || undefined}
                      aria-label={`${shop.name}${shop.neighborhood ? `, ${shop.neighborhood}` : ''}`}
                    >
                      {shop.logo ? <img src={shop.logo} alt="" loading="lazy" /> : <span className="shops-slot-initial">{shop.name[0]}</span>}
                      <strong>{shop.name}</strong>
                      {shop.neighborhood && <span>{shop.neighborhood}</span>}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Built here (+ the deal, when SHOW_DEAL) */}
        <section className="shops-section shops-section--band" aria-label={SHOW_DEAL ? 'Who builds it and the deal' : 'Who builds it'}>
          <div className={`shops-wrap shops-split${SHOW_DEAL ? '' : ' shops-split--single'}`}>
            <div className="shops-card shops-built">
              <h2>Built in the Twin Cities</h2>
              <p>
                On-Screen is a small, independent company based in St. Louis Park. Support comes
                from the people who build it, not a ticket queue.
              </p>
              <div className="shops-built-links">
                <Link to="/about-me">Read the founder&rsquo;s story</Link>
                {FOUNDER_INSTAGRAM && (
                  <a href={`https://instagram.com/${FOUNDER_INSTAGRAM}`} target="_blank" rel="noopener noreferrer">
                    @{FOUNDER_INSTAGRAM}
                  </a>
                )}
              </div>
            </div>

            {SHOW_DEAL && (
              <div className="shops-card shops-deal">
                <h2>Founding shops get it free. <span className="shops-accent">For life.</span></h2>
                <ul>
                  {DEAL.map((d) => <li key={d}>{d}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Closing call to action */}
        <section id="claim" className="shops-section shops-claim" aria-labelledby="shops-claim-h">
          <div className="shops-wrap shops-claim-inner">
            <div className="shops-claim-copy">
              <h2 id="shops-claim-h">Have an event coming up?</h2>
              <p>Send the date and we&rsquo;ll help you set it up.</p>
            </div>

            {status === 'sent' ? (
              <div className="shops-card shops-sent" role="status">
                <h3>Thanks. You&rsquo;re on the list.</h3>
                <p>We&rsquo;ll reach out within a day to set up your event.</p>
              </div>
            ) : (
              <form className="shops-card shops-form" onSubmit={submit}>
                <label>
                  <span>Shop name</span>
                  <input name="shopName" value={form.shopName} onChange={update('shopName')} required maxLength={150} autoComplete="organization" />
                </label>
                <label>
                  <span>Instagram handle <em>optional</em></span>
                  <input name="instagram" value={form.instagram} onChange={update('instagram')} maxLength={31} placeholder="@yourshop" autoCapitalize="none" />
                </label>
                <label>
                  <span>Your next event date <em>optional</em></span>
                  <input name="nextEventDate" type="date" value={form.nextEventDate} onChange={update('nextEventDate')} />
                </label>
                <label>
                  <span>Best way to reach you</span>
                  <input name="contact" value={form.contact} onChange={update('contact')} required maxLength={200} placeholder="Phone, email, or DM us" />
                </label>
                {/* Honeypot — hidden from people, irresistible to bots. */}
                <label className="shops-hp" aria-hidden="true">
                  <span>Website</span>
                  <input name="website" value={form.website} onChange={update('website')} tabIndex={-1} autoComplete="off" />
                </label>

                {status === 'error' && <p className="shops-form-error" role="alert">{error}</p>}

                <button type="submit" className="shops-btn shops-btn--primary shops-btn--block" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Claim a spot'}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <footer className="shops-footer">
        <div className="shops-wrap shops-footer-inner">
          <span>© {new Date().getFullYear()} On-Screen · St. Louis Park, MN</span>
          <nav aria-label="Legal">
            <Link to="/privacypolicy">Privacy</Link>
            <Link to="/termsofuse">Terms</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
